import React, { useState, useEffect, useRef } from 'react';
import ExcelJS from 'exceljs';
import reportService from '../../services/reportService';
import { getWarehouses } from '../../services/warehousesService';
import { authService } from '../../services/authService';
import { filterWarehousesForUser, getLimitedUserScope } from '../../utils/limitedUserAccess';
import Toast from '../../components/Common/Toast';

const EntriesExits = () => {
  const [reportData, setReportData] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [warehousesLoaded, setWarehousesLoaded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState({ isOpen: false, message: '', type: 'info' });
  
  // Refs para evitar llamadas duplicadas
  const hasLoadedData = useRef(false);
  
  // Filtros
  const now = new Date();
  const [selectedYear, setSelectedYear] = useState(now.getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(now.getMonth() + 1);
  const [selectedWarehouse, setSelectedWarehouse] = useState('');
  const [viewFromStart, setViewFromStart] = useState(false);
  
  const monthNames = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
  ];
  
  const years = [];
  for (let i = now.getFullYear(); i >= now.getFullYear() - 5; i--) {
    years.push(i);
  }

  useEffect(() => {
    if (!hasLoadedData.current) {
      hasLoadedData.current = true;
      loadWarehouses();
    }
  }, []);

  useEffect(() => {
    if (warehousesLoaded) {
      loadReport();
    }
  }, [selectedYear, selectedMonth, selectedWarehouse, viewFromStart, warehousesLoaded]);

  const loadWarehouses = async () => {
    try {
      const response = await getWarehouses({ active_only: true });
      if (response.success) {
        const user = authService.getCurrentUser();
        const filtered = filterWarehousesForUser(response.data || [], user);
        setWarehouses(filtered);
        if (filtered.length === 1) {
          setSelectedWarehouse(String(filtered[0].id));
        }
      }
    } catch (error) {
      console.error('Error al cargar almacenes:', error);
      setToast({
        isOpen: true,
        message: 'Error al cargar almacenes',
        type: 'error'
      });
    } finally {
      setWarehousesLoaded(true);
    }
  };

  const loadReport = async () => {
    try {
      setLoading(true);
      const response = await reportService.getEntriesExitsReport(
        selectedYear,
        selectedMonth,
        selectedWarehouse || null,
        viewFromStart
      );
      
      if (response.success) {
        setReportData(response.data || []);
      } else {
        setToast({
          isOpen: true,
          message: response.message || 'Error al cargar el reporte',
          type: 'error'
        });
      }
    } catch (error) {
      console.error('Error al cargar reporte:', error);
      const serverMessage = error.response?.data?.message;
      setToast({
        isOpen: true,
        message: serverMessage || 'Error al cargar el reporte de entradas y salidas',
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const exportToExcel = async () => {
    try {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Entradas y Salidas');

      // Estilos
      const titleStyle = {
        font: { bold: true, size: 16, color: { argb: 'FF1F4E78' } },
        alignment: { horizontal: 'center', vertical: 'middle' },
        fill: {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FFE7F3FF' }
        }
      };

      const headerStyle = {
        font: { bold: true, size: 11, color: { argb: 'FFFFFFFF' } },
        alignment: { horizontal: 'center', vertical: 'middle', wrapText: true },
        fill: {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FF4472C4' }
        },
        border: {
          top: { style: 'thin', color: { argb: 'FF000000' } },
          bottom: { style: 'thin', color: { argb: 'FF000000' } },
          left: { style: 'thin', color: { argb: 'FF000000' } },
          right: { style: 'thin', color: { argb: 'FF000000' } }
        }
      };

      const dataStyle = {
        alignment: { vertical: 'middle' },
        border: {
          top: { style: 'thin', color: { argb: 'FFCCCCCC' } },
          bottom: { style: 'thin', color: { argb: 'FFCCCCCC' } },
          left: { style: 'thin', color: { argb: 'FFCCCCCC' } },
          right: { style: 'thin', color: { argb: 'FFCCCCCC' } }
        }
      };

      const numberStyle = {
        ...dataStyle,
        numFmt: '#,##0.000'
      };

      const currencyStyle = {
        ...dataStyle,
        // Mostrar montos con 4 decimales
        numFmt: '"S/"#,##0.0000'
      };

      const totalStyle = {
        font: { bold: true, size: 11 },
        fill: {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FFF2F2F2' }
        },
        border: {
          top: { style: 'medium', color: { argb: 'FF000000' } },
          bottom: { style: 'medium', color: { argb: 'FF000000' } },
          left: { style: 'thin', color: { argb: 'FF000000' } },
          right: { style: 'thin', color: { argb: 'FF000000' } }
        }
      };

      // Título
      worksheet.mergeCells('A1:K1');
      const titleCell = worksheet.getCell('A1');
      titleCell.value = `Reporte de Entradas y Salidas - ${monthNames[selectedMonth - 1]} ${selectedYear}`;
      titleCell.style = titleStyle;
      worksheet.getRow(1).height = 25;

      // Información adicional
      worksheet.mergeCells('A2:K2');
      const infoCell = worksheet.getCell('A2');
      infoCell.value = `Generado el ${new Date().toLocaleDateString('es-PE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}`;
      infoCell.style = {
        font: { size: 10, italic: true, color: { argb: 'FF666666' } },
        alignment: { horizontal: 'center', vertical: 'middle' }
      };
      worksheet.getRow(2).height = 20;

      // Encabezados - Primero cantidades, luego montos
      const headers = [
        { header: 'Producto', key: 'product_name', width: 35 },
        { header: 'SKU', key: 'product_sku', width: 20 },
        { header: 'Almacén', key: 'warehouse_name', width: 25 },
        { header: 'Saldo Anterior\n(Cantidad)', key: 'previous_qty', width: 18 },
        { header: 'Entradas\n(Cantidad)', key: 'entries_qty', width: 18 },
        { header: 'Salidas\n(Cantidad)', key: 'exits_qty', width: 18 },
        { header: 'Saldo Final\n(Cantidad)', key: 'final_qty', width: 18 },
        { header: 'Saldo Anterior\n(Monto)', key: 'previous_amt', width: 18 },
        { header: 'Entradas\n(Monto)', key: 'entries_amt', width: 18 },
        { header: 'Salidas\n(Monto)', key: 'exits_amt', width: 18 },
        { header: 'Saldo Final\n(Monto)', key: 'final_amt', width: 18 }
      ];

      worksheet.columns = headers;
      const headerRow = worksheet.getRow(3);
      headerRow.height = 40;
      
      headers.forEach((col, index) => {
        const cell = headerRow.getCell(index + 1);
        cell.style = headerStyle;
        cell.value = col.header;
      });

      // Datos - Primero cantidades, luego montos
      let dataRowIndex = 4;
      reportData.forEach((item, index) => {
        const row = worksheet.addRow([
          item.product_name || '',
          item.product_sku || '',
          item.warehouse_name || '',
          // Cantidades
          item.previous_balance_quantity || 0,
          item.entries_quantity || 0,
          item.exits_quantity || 0,
          item.final_balance_quantity || 0,
          // Montos
          item.previous_balance_amount || 0,
          item.entries_amount || 0,
          item.exits_amount || 0,
          item.final_balance_amount || 0
        ]);

        row.height = 20;

        // Aplicar estilos a cada celda
        row.eachCell({ includeEmpty: false }, (cell, colNumber) => {
          if (colNumber <= 3) {
            // Columnas de texto (Producto, SKU, Almacén)
            cell.style = dataStyle;
          } else if (colNumber >= 4 && colNumber <= 7) {
            // Columnas de cantidad (4-7: Saldo Anterior, Entradas, Salidas, Saldo Final - Cantidades)
            cell.style = numberStyle;
          } else {
            // Columnas de monto (8-11: Saldo Anterior, Entradas, Salidas, Saldo Final - Montos)
            cell.style = currencyStyle;
          }

          // Alternar color de fila
          if (index % 2 === 0) {
            cell.fill = {
              type: 'pattern',
              pattern: 'solid',
              fgColor: { argb: 'FFF9F9F9' }
            };
          }
        });
      });

      // Congelar paneles (fijar encabezados)
      worksheet.views = [
        {
          state: 'frozen',
          ySplit: 3,
          activeCell: 'A4',
          showGridLines: true
        }
      ];

      // Generar archivo
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Entradas_y_Salidas_${monthNames[selectedMonth - 1]}_${selectedYear}.xlsx`;
      link.click();
      window.URL.revokeObjectURL(url);

      setToast({
        isOpen: true,
        message: 'Reporte exportado exitosamente',
        type: 'success'
      });
    } catch (error) {
      console.error('Error al exportar:', error);
      setToast({
        isOpen: true,
        message: 'Error al exportar el reporte',
        type: 'error'
      });
    }
  };

  const isLimitedWithWarehouses = getLimitedUserScope(authService.getCurrentUser()).showAlmacenes;

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Reporte de Entradas y Salidas</h1>
        <p className="text-gray-600">
          Resumen de movimientos por producto y almacén para {monthNames[selectedMonth - 1]} {selectedYear}
        </p>
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-700">Mes:</label>
            <select
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
            >
              {monthNames.map((month, index) => (
                <option key={index + 1} value={index + 1}>{month}</option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-700">Año:</label>
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(parseInt(e.target.value))}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
            >
              {years.map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-700">Almacén:</label>
            <select
              value={selectedWarehouse}
              onChange={(e) => setSelectedWarehouse(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
            >
              {!isLimitedWithWarehouses || warehouses.length > 1 ? (
                <option value="">
                  {isLimitedWithWarehouses ? 'Todos mis almacenes' : 'Todos los almacenes'}
                </option>
              ) : null}
              {warehouses.map(wh => (
                <option key={wh.id} value={wh.id}>{wh.code} - {wh.name}</option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="viewFromStart"
              checked={viewFromStart}
              onChange={(e) => setViewFromStart(e.target.checked)}
              className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
            />
            <label htmlFor="viewFromStart" className="text-sm font-medium text-gray-700 cursor-pointer">
              Ver desde el inicio
            </label>
          </div>
          <button
            onClick={exportToExcel}
            className="ml-auto px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
          >
            Exportar a Excel
          </button>
        </div>
      </div>

      {/* Tabla */}
      {loading ? (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
          <p className="mt-2 text-gray-600">Cargando reporte...</p>
        </div>
      ) : reportData.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
          <p className="text-gray-500">No hay datos para el período seleccionado</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Producto</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">SKU</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Almacén</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Saldo Anterior</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Entradas</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Salidas</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Saldo Final</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {reportData.map((item, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                      {item.product_name || '-'}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                      {item.product_sku || '-'}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                      {item.warehouse_name || '-'}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 text-right">
                      <div className="flex flex-col items-end">
                        <span className="font-medium">
                          {(item.previous_balance_quantity || 0).toLocaleString('es-PE', { minimumFractionDigits: 3, maximumFractionDigits: 3 })}
                        </span>
                        <span className="text-xs text-gray-500">
                          S/ {(item.previous_balance_amount || 0).toLocaleString('es-PE', { minimumFractionDigits: 4, maximumFractionDigits: 4 })}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-green-600 text-right">
                      <div className="flex flex-col items-end">
                        <span className="font-medium">
                          {(item.entries_quantity || 0).toLocaleString('es-PE', { minimumFractionDigits: 3, maximumFractionDigits: 3 })}
                        </span>
                        <span className="text-xs text-gray-500">
                          S/ {(item.entries_amount || 0).toLocaleString('es-PE', { minimumFractionDigits: 4, maximumFractionDigits: 4 })}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-red-600 text-right">
                      <div className="flex flex-col items-end">
                        <span className="font-medium">
                          {(item.exits_quantity || 0).toLocaleString('es-PE', { minimumFractionDigits: 3, maximumFractionDigits: 3 })}
                        </span>
                        <span className="text-xs text-gray-500">
                          S/ {(item.exits_amount || 0).toLocaleString('es-PE', { minimumFractionDigits: 4, maximumFractionDigits: 4 })}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm font-semibold text-gray-900 text-right">
                      <div className="flex flex-col items-end">
                        <span className="font-bold">
                          {(item.final_balance_quantity || 0).toLocaleString('es-PE', { minimumFractionDigits: 3, maximumFractionDigits: 3 })}
                        </span>
                        <span className="text-xs text-gray-500">
                          S/ {(item.final_balance_amount || 0).toLocaleString('es-PE', { minimumFractionDigits: 4, maximumFractionDigits: 4 })}
                        </span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <Toast
        isOpen={toast.isOpen}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ ...toast, isOpen: false })}
      />
    </div>
  );
};

export default EntriesExits;
